import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { VIVU_ADMIN_USER_ID } from "@/lib/constants";

export interface SystemModule {
  id: string;
  module_key: string;
  name: string;
  description: string | null;
  category: string;
  icon: string | null;
  route: string | null;
  display_order: number;
  is_active: boolean;
}

export interface SystemFeature {
  id: string;
  module_id: string;
  feature_key: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
}

export interface RolePermission {
  id: string;
  role: string;
  module_id: string;
  feature_id: string | null;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
  can_export: boolean;
}

export interface UserPermissionOverride {
  id: string;
  user_id: string;
  module_id: string;
  feature_id: string | null;
  can_read: boolean | null;
  can_create: boolean | null;
  can_update: boolean | null;
  can_delete: boolean | null;
  can_export: boolean | null;
  override_reason: string | null;
  granted_by: string | null;
  granted_at: string;
  expires_at: string | null;
}

export const usePermissions = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all modules
  const { data: modules, isLoading: modulesLoading } = useQuery({
    queryKey: ["system-modules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_modules")
        .select("*")
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as SystemModule[];
    },
  });

  // Fetch features for a specific module
  const useModuleFeatures = (moduleId: string | null) => {
    return useQuery({
      queryKey: ["system-features", moduleId],
      queryFn: async () => {
        if (!moduleId) return [];
        
        const { data, error } = await supabase
          .from("system_features")
          .select("*")
          .eq("module_id", moduleId)
          .order("display_order", { ascending: true });
        
        if (error) throw error;
        return data as SystemFeature[];
      },
      enabled: !!moduleId,
    });
  };

  // Fetch role permissions
  const useRolePermissions = (role: string | null) => {
    return useQuery({
      queryKey: ["role-permissions", role],
      queryFn: async () => {
        if (!role) return [];
        
        const { data, error } = await supabase
          .from("role_permissions")
          .select("*")
          .eq("role", role as any);
        
        if (error) throw error;
        return data as RolePermission[];
      },
      enabled: !!role,
    });
  };

  // Fetch user permission overrides
  const useUserOverrides = (userId: string | null) => {
    return useQuery({
      queryKey: ["user-permission-overrides", userId],
      queryFn: async () => {
        if (!userId) return [];
        
        const { data, error } = await supabase
          .from("user_permission_overrides")
          .select("*")
          .eq("user_id", userId);
        
        if (error) throw error;
        return data as UserPermissionOverride[];
      },
      enabled: !!userId,
    });
  };

  // Update role permission
  const updateRolePermission = useMutation({
    mutationFn: async (params: {
      role: string;
      moduleId: string;
      featureId: string | null;
      permissions: Partial<{
        can_read: boolean;
        can_create: boolean;
        can_update: boolean;
        can_delete: boolean;
        can_export: boolean;
      }>;
      existingId?: string;
    }) => {
      // If a permission row already exists, perform an UPDATE to avoid duplicates
      if (params.existingId) {
        const { data, error } = await supabase
          .from("role_permissions")
          .update({
            ...params.permissions,
          })
          .eq("id", params.existingId)
          .select()
          .single();

        if (error) throw error;
        return data;
      }

      // Otherwise insert a new row (module-/feature-level)
      const { data, error } = await supabase
        .from("role_permissions")
        .insert({
          role: params.role as any,
          module_id: params.moduleId,
          feature_id: params.featureId,
          ...params.permissions,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["role-permissions", variables.role] });
      toast.success("Berechtigungen aktualisiert");
    },
    onError: (error) => {
      toast.error("Fehler beim Aktualisieren der Berechtigungen");
      console.error(error);
    },
  });

  // Add user permission override
  const addUserOverride = useMutation({
    mutationFn: async (params: {
      userId: string;
      moduleId: string;
      featureId: string | null;
      permissions: Partial<{
        can_read: boolean;
        can_create: boolean;
        can_update: boolean;
        can_delete: boolean;
        can_export: boolean;
      }>;
      reason: string;
      expiresAt?: string;
    }) => {
      const { data, error } = await supabase
        .from("user_permission_overrides")
        .insert({
          user_id: params.userId,
          module_id: params.moduleId,
          feature_id: params.featureId,
          ...params.permissions,
          override_reason: params.reason,
          granted_by: user?.id,
          expires_at: params.expiresAt,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user-permission-overrides", variables.userId] });
      toast.success("Benutzerberechtigungen überschrieben");
    },
    onError: (error) => {
      toast.error("Fehler beim Überschreiben der Berechtigungen");
      console.error(error);
    },
  });

  // Remove user permission override
  const removeUserOverride = useMutation({
    mutationFn: async (overrideId: string) => {
      const { error } = await supabase
        .from("user_permission_overrides")
        .delete()
        .eq("id", overrideId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-permission-overrides"] });
      toast.success("Überschreibung entfernt");
    },
    onError: (error) => {
      toast.error("Fehler beim Entfernen der Überschreibung");
      console.error(error);
    },
  });

  // Check if user can access a module/feature
  const canAccess = async (
    moduleKey: string, 
    action: string = "read",
    featureKey?: string
  ) => {
    if (!user?.id) return false;

    // Admin bypass: skip RPC call for admin users (UUID-based check)
    if (user.id === VIVU_ADMIN_USER_ID) {
      return true;
    }

    const { data, error } = await supabase.rpc("user_can_access_module", {
      _user_id: user.id,
      _module_key: moduleKey,
      _action: action,
      _feature_key: featureKey || null,
    });

    if (error) {
      console.error("Permission check error:", error);
      return false;
    }

    return data as boolean;
  };

  // Batch-fetch all accessible modules with React Query for caching
  const { data: accessibleModules, isLoading: accessibleModulesLoading } = useQuery({
    queryKey: ["accessible-modules", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return new Set<string>();
      }

      const { data, error } = await supabase.rpc("user_accessible_modules", {
        _user_id: user.id,
      });

      if (error) {
        console.error("[usePermissions] Batch permission check error:", error);
        return new Set<string>();
      }

      // Convert to Set of module_keys where can_read is true
      const accessible = new Set<string>();
      (data || []).forEach((row: any) => {
        if (row.can_read) {
          accessible.add(row.module_key);
        }
      });

      return accessible;
    },
    enabled: !!user?.id,
    staleTime: 15 * 60 * 1000, // 15 minutes - Permissions
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Cache for individual permission checks
  const usePermissionCheck = (moduleKey: string, action: string = "read", featureKey?: string) => {
    return useQuery({
      queryKey: ["permission-check", user?.id, moduleKey, action, featureKey],
      queryFn: async () => {
        if (!user?.id) return false;

        // Admin bypass (UUID-based check)
        if (user.id === VIVU_ADMIN_USER_ID) {
          return true;
        }

        const { data, error } = await supabase.rpc("user_can_access_module", {
          _user_id: user.id,
          _module_key: moduleKey,
          _action: action,
          _feature_key: featureKey || null,
        });

        if (error) {
          console.error("Permission check error:", error);
          return false;
        }

        return data as boolean;
      },
      enabled: !!user?.id,
      staleTime: 15 * 60 * 1000, // 15 minutes - Permissions
      gcTime: 30 * 60 * 1000, // 30 minutes
    });
  };

  return {
    modules,
    modulesLoading,
    useModuleFeatures,
    useRolePermissions,
    useUserOverrides,
    updateRolePermission,
    addUserOverride,
    removeUserOverride,
    canAccess,
    accessibleModules: accessibleModules || new Set<string>(),
    accessibleModulesLoading,
    usePermissionCheck,
  };
};